import { useState, useCallback, useMemo } from 'react';
import type { TablePaginationConfig } from 'antd';
import type { PagedResponse } from '@/api/types';

export interface UsePaginationOptions {
  initialPage?: number;
  initialPageSize?: number;
}

export interface UsePaginationReturn {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setTotal: (total: number) => void;
  updateFromResponse: <T>(response: PagedResponse<T>) => void;
  antdPaginationProps: TablePaginationConfig;
  /** Converts 1-based page to 0-based `from` offset for backend */
  toSearchFrom: () => number;
}

export function usePagination(options?: UsePaginationOptions): UsePaginationReturn {
  const { initialPage = 1, initialPageSize = 20 } = options ?? {};

  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const [total, setTotal] = useState(0);

  const totalPages = useMemo(
    () => (pageSize > 0 ? Math.ceil(total / pageSize) : 0),
    [total, pageSize]
  );

  const setPageSize = useCallback((size: number) => {
    setPageSizeState(size);
    setPage(1); // Reset to first page on size change
  }, []);

  const updateFromResponse = useCallback(<T,>(response: PagedResponse<T>) => {
    setTotal(response.total);
    setPage(response.page);
    setPageSizeState(response.page_size);
  }, []);

  const toSearchFrom = useCallback(() => {
    return (page - 1) * pageSize;
  }, [page, pageSize]);

  const antdPaginationProps: TablePaginationConfig = useMemo(() => ({
    current: page,
    pageSize,
    total,
    showSizeChanger: true,
    showTotal: (total: number, range: [number, number]) => `${range[0]}-${range[1]} / ${total}`,
    onChange: (newPage: number, newPageSize: number) => {
      if (newPageSize !== pageSize) {
        setPageSize(newPageSize);
      } else {
        setPage(newPage);
      }
    },
  }), [page, pageSize, total, setPageSize]);

  return {
    page,
    pageSize,
    total,
    totalPages,
    setPage,
    setPageSize,
    setTotal,
    updateFromResponse,
    antdPaginationProps,
    toSearchFrom,
  };
}
